import { useCallback, useState } from 'react'
import { useQueryClient } from 'react-query'

import { Contact } from '@features/contacts/types/contact'

import { QueryKeys } from '../../../../../enums/queryKeys'
import { useUpdateContactWithExternalId } from './useUpdateContactWithExternalId'
import { useUpdateReceiverDependentValues } from './useUpdateReceiverDependentValues'

const defaultPendingContactRequest = { attempt: 0 }

export const useUpdateReceiverOnCustomerSave = () => {
  // Store both id and an attempt counter so that calling updateReceiverOnCustomerSave
  // with the same id twice in a row still triggers a state change and re-enables the query.
  // Without the counter, React would bail out of re-rendering since the id hasn't changed,
  // and the polling cycle would not restart.
  const [pendingContactRequest, setPendingContactRequest] = useState<{ id?: string; attempt: number }>(
    defaultPendingContactRequest,
  )
  const queryClient = useQueryClient()

  const { handleReceiverUpdate } = useUpdateReceiverDependentValues()

  const onContactUpdate = useCallback(
    (contact: Contact) => {
      queryClient.invalidateQueries([QueryKeys.ContactsAndContactPersons])
      handleReceiverUpdate(contact)
      setPendingContactRequest(defaultPendingContactRequest)
    },
    [handleReceiverUpdate, queryClient],
  )

  const { isProcessing } = useUpdateContactWithExternalId({
    contactId: pendingContactRequest?.id,
    onContactUpdate,
    enabled: !!pendingContactRequest?.id,
  })

  const updateReceiverOnCustomerSave = useCallback((id: string) => {
    // Increment attempt on every call to guarantee a state update even when the id is the same.
    setPendingContactRequest((prev) => ({ id, attempt: prev.attempt + 1 }))
  }, [])

  return { updateReceiverOnCustomerSave, isProcessing }
}
